import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Auth.css';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { registerEmail, loginGoogle, user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      navigate(user.email_verified === false ? '/check-email' : '/app', { replace: true });
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const u = await registerEmail(formData.email, formData.password, formData.name);
      navigate(u.email_verified === false ? '/check-email' : '/app', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-background">
        <video
          className="auth-video"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="/hero_video.mp4" type="video/mp4" />
        </video>
        <div className="auth-video-overlay"></div>
      </div>

      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <Link to="/" className="auth-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_5fec953c-1553-4442-b50d-48094837ff2f/artifacts/yv8qmlbd_AI_Architecture_Logo__2_-removebg-preview.png"
                alt="Ezai Build"
                className="logo-icon"
                style={{ width: 44, height: 44 }}
              />
              <span className="logo-text">Ezai Build</span>
            </Link>
            <h1 className="display-medium auth-title">Create Account</h1>
            <p className="body-medium auth-subtitle">
              Join thousands of architects creating amazing 3D visualizations
            </p>
          </div>

          <form onSubmit={handleRegister} className="auth-form">
            <div className="form-group">
              <label className="form-label body-medium">Full Name</label>
              <div className="input-wrapper">
                <User size={20} className="input-icon" />
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label body-medium">Email</label>
              <div className="input-wrapper">
                <Mail size={20} className="input-icon" />
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label body-medium">Password</label>
              <div className="input-wrapper">
                <Lock size={20} className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="form-input"
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label body-medium">Confirm Password</label>
              <div className="input-wrapper">
                <Lock size={20} className="input-icon" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  className="form-input"
                  placeholder="Re-enter your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  data-testid="register-confirm-password-input"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="form-footer">
              <label className="checkbox-label body-small">
                <input type="checkbox" required />
                <span>
                  I agree to the{' '}
                  <Link to="#" className="auth-link">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="#" className="auth-link">
                    Privacy Policy
                  </Link>
                </span>
              </label>
            </div>

            {error && (
              <p className="auth-error body-small" data-testid="auth-error">{error}</p>
            )}

            <Button
              type="submit"
              className="btn-primary btn-large auth-btn"
              disabled={submitting}
              data-testid="register-submit-btn"
            >
              {submitting ? <Loader2 size={20} className="spinner" /> : 'Create Account'}
            </Button>
          </form>

          <div className="auth-divider">
            <span className="body-small">or continue with</span>
          </div>

          <div className="social-buttons">
            <button
              type="button"
              className="social-btn"
              onClick={loginGoogle}
              data-testid="google-register-btn"
            >
              <img src="https://www.google.com/favicon.ico" alt="Google" />
              <span className="body-medium">Google</span>
            </button>
          </div>

          <p className="auth-switch body-medium">
            Already have an account?{' '}
            <Link to="/login" className="auth-link">
              Sign in
            </Link>
          </p>
        </div>

        <div className="auth-info">
          <div className="info-card">
            <h3 className="heading-2">Why Choose Ezai Build?</h3>
            <ul className="info-list">
              <li className="body-medium">
                Lightning-fast 3D generation
              </li>
              <li className="body-medium">
                Multiple material options
              </li>
              <li className="body-medium">
                Support for DWG, DXF & more
              </li>
              <li className="body-medium">
                Unlimited project storage
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
